import * as React from 'react'
import cx from 'classnames'
import { NEVER, Observable } from 'rxjs'
import Icon from 'components/Icon'
import { useObservableFocus } from 'hooks/focus-hooks'
import styles from './ToggleGroup.sass'

interface ToggleGroupProps {
  children?: React.ReactNode
  stretch?: boolean
  multi?: boolean
  focus$?: Observable<unknown>
  onFocus?: () => unknown
}

const ToggleGroupPropsContext = React.createContext<ToggleGroupProps>({})

export function ToggleGroup({
  children,
  multi,
  stretch,
  focus$ = NEVER,
  onFocus,
}: ToggleGroupProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const focusGroup = React.useCallback(
    () => (rootRef.current?.firstChild as HTMLElement)?.focus?.(),
    [],
  )
  useObservableFocus(focus$, focusGroup)
  return (
    <ToggleGroupPropsContext.Provider
      value={{ multi, stretch, focus$, onFocus }}
    >
      <div
        ref={rootRef}
        className={cx(styles.root, stretch && styles.stretched)}
        role={multi ? 'radiogroup' : 'listbox'}
      >
        {children}
      </div>
    </ToggleGroupPropsContext.Provider>
  )
}

interface ToggleItemProps {
  children?: React.ReactNode
  toggled?: boolean
  disabled?: boolean
  isNotValid?: boolean
  title?: string
  onKeyDown?: React.KeyboardEventHandler<unknown>
  onChange?: (newValue: boolean) => unknown
  dataTest?: string
  onFocus?: () => unknown
}

export function ToggleItem({
  children,
  toggled,
  onChange,
  disabled,
  isNotValid,
  title,
  onKeyDown,
  dataTest,
  onFocus,
}: ToggleItemProps) {
  const { multi, onFocus: ctxOnFocus } = React.useContext(
    ToggleGroupPropsContext,
  )
  const change = React.useCallback(() => {
    if (!disabled) {
      onChange?.(!toggled)
    }
  }, [toggled, disabled, onChange])
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      onKeyDown?.(e)
      if (e.defaultPrevented) {
        return
      }
      // Enter
      if (e.key === 'Enter') {
        e.preventDefault()
      }
      // Space
      if (e.key === ' ') {
        e.preventDefault()
        change()
      }
      // Arrow up
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        ;(e.currentTarget.previousSibling as HTMLElement)?.focus?.()
      }
      // Arrow down
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        ;(e.currentTarget.nextSibling as HTMLElement)?.focus?.()
      }
    },
    [change],
  )

  const focus = React.useCallback(() => {
    onFocus?.()
    ctxOnFocus?.()
  }, [ctxOnFocus, onFocus])
  return (
    <a
      onClick={change}
      title={title}
      tabIndex={0}
      aria-checked={!!toggled}
      onKeyDown={handleKeyDown}
      className={cx(
        styles.item,
        toggled && styles.toggled,
        disabled && styles.disabled,
        isNotValid && styles.isNotValid,
        multi && styles.multi,
      )}
      data-test={dataTest}
      onFocus={focus}
      {...(multi ? { role: 'checkbox' } : { role: 'radio' })}
    >
      <div className={cx(styles.toggledIcon, toggled && styles.pop)}>
        <Icon
          auto
          className={cx(disabled && styles.disabledIcon)}
          name={
            multi
              ? toggled
                ? 'checkbox'
                : 'checkbox-blank'
              : toggled
                ? 'radio-selected'
                : 'radio'
          }
        />
      </div>
      <div className={styles.itemContent}>{children}</div>
    </a>
  )
}
